import { useCallback, useEffect, useState } from 'react';
import { copyToClipboard } from '../lib/clipboard';

const DELETED_PREFIX = 'tizok-deleted-';

function loadDeleted(roomId: string): string[] {
  try {
    const raw = localStorage.getItem(`${DELETED_PREFIX}${roomId}`);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

interface UseMessageActionsArgs {
  roomId: string;
  currentUserId: string;
  /** ms before the "copied" feedback flag auto-clears. */
  copiedResetMs?: number;
}

/**
 * Client-side state for a per-message action menu: which message's menu is
 * open, copy-to-clipboard with transient "copied" feedback, and delete-own.
 * Deletes are an optimistic client-side hide persisted to localStorage
 * (`tizok-deleted-{roomId}`); server-side delete is deferred to V1.5+, matching
 * useReactions / useMessageEdits.
 */
export function useMessageActions({
  roomId,
  currentUserId,
  copiedResetMs = 2000,
}: UseMessageActionsArgs) {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [copyFailedId, setCopyFailedId] = useState<string | null>(null);
  const [deletedIds, setDeletedIds] = useState<string[]>(() => loadDeleted(roomId));

  // Re-sync the deleted set and drop transient menu/copied state on room switch.
  useEffect(() => {
    setDeletedIds(loadDeleted(roomId));
    setOpenMenuId(null);
    setCopiedId(null);
    setCopyFailedId(null);
  }, [roomId]);

  // Auto-clear the "copied" feedback after a beat.
  useEffect(() => {
    if (copiedId === null) return;
    const timer = setTimeout(() => setCopiedId(null), copiedResetMs);
    return () => clearTimeout(timer);
  }, [copiedId, copiedResetMs]);

  // Auto-clear the "copy failed" feedback after a beat, too.
  useEffect(() => {
    if (copyFailedId === null) return;
    const timer = setTimeout(() => setCopyFailedId(null), copiedResetMs);
    return () => clearTimeout(timer);
  }, [copyFailedId, copiedResetMs]);

  const openMenu = useCallback((messageId: string) => setOpenMenuId(messageId), []);
  const closeMenu = useCallback(() => setOpenMenuId(null), []);

  const canDelete = useCallback(
    (senderUserId: string) => senderUserId === currentUserId,
    [currentUserId],
  );

  const copyText = useCallback(async (messageId: string, text: string) => {
    if (await copyToClipboard(text)) {
      setCopiedId(messageId);
      setCopyFailedId(null);
    } else {
      // The message body is already on screen and selectable, so surface a
      // recoverable hint rather than failing silently.
      setCopyFailedId(messageId);
      setCopiedId(null);
    }
    // Always close the menu: a failed copy must not leave it stuck open.
    setOpenMenuId(null);
  }, []);

  const deleteMessage = useCallback(
    (messageId: string) => {
      setDeletedIds((prev) => {
        if (prev.includes(messageId)) return prev;
        const next = [...prev, messageId];
        localStorage.setItem(`${DELETED_PREFIX}${roomId}`, JSON.stringify(next));
        return next;
      });
      setOpenMenuId(null);
    },
    [roomId],
  );

  const isDeleted = useCallback(
    (messageId: string) => deletedIds.includes(messageId),
    [deletedIds],
  );

  return {
    openMenuId,
    openMenu,
    closeMenu,
    canDelete,
    copyText,
    copiedId,
    copyFailedId,
    deleteMessage,
    isDeleted,
  };
}
